// Nyafaz engagement API client (enterprise_analyze / engagement_*).
//
// Who the caller is and which branches they see is decided by the server from the portal sign-in (the
// HttpOnly portal cookie and X-Enterprise-Portal header, which the portals' fetch interceptor adds). The only
// scope the screens send is the branch list HQ picked: franchise_id for one branch, franchise_ids for several.
import { getEngagementPortal } from '../engagementPortal';
import { PORTAL_HEADER } from '../../admin-shared/storage';

const ENTERPRISE_ENGAGEMENT_API_PATH = '/enterprise-engagement-api';

function isEnterpriseEngagementUi() {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname || '';
  return (
    path.includes('/nyafaz-ep/partner/') ||
    path.includes('/nyafaz-ep/workspace/engagement') ||
    path.includes('/nyafaz-ep/workspace/whatsapp') ||
    path.startsWith('/nyafaz-ep/engagement')
  );
}

function defaultLocalEngagementBase() {
  if (typeof window === 'undefined') return 'http://localhost:8021';
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') {
    return 'http://localhost:8021';
  }
  return `${window.location.origin}${ENTERPRISE_ENGAGEMENT_API_PATH}`;
}

function normalizeEngagementApiEnv(raw) {
  const env = String(raw || '').trim().replace(/\/+$/, '');
  if (!env) return '';
  if (env.includes('demo-engagement-api')) return '';
  return env;
}

/** Nyafaz engagement API only (/enterprise-engagement-api → app_engagement.py, enterprise_analyze). */
export function getEngagementApiBase() {
  if (isEnterpriseEngagementUi()) {
    return `${window.location.origin}${ENTERPRISE_ENGAGEMENT_API_PATH}`.replace(/\/+$/, '');
  }
  const env = normalizeEngagementApiEnv(process.env.REACT_APP_ENTERPRISE_ENGAGEMENT_API_URL);
  if (env) {
    if (env.startsWith('http')) return env;
    if (typeof window !== 'undefined') {
      const prefix = env.startsWith('/') ? env : `/${env}`;
      return `${window.location.origin}${prefix}`.replace(/\/+$/, '');
    }
    return env;
  }
  return defaultLocalEngagementBase().replace(/\/+$/, '');
}

export const SALON_NAME = process.env.REACT_APP_ENTERPRISE_ENGAGEMENT_SALON_NAME || 'Nyafaz';

/** A link the browser can open: a backend path (such as a call recording kept on the server) gets the API address. */
export function backendUrl(url) {
  const base = getEngagementApiBase();
  return url && url.startsWith('/') ? `${base}${url}` : url;
}

// Branches checked in HQ's branch picker ([] = all branches). Set by EngagementScopeProvider.
let selectedBranches = [];

export function setSelectedBranches(ids) {
  const next = [];
  (Array.isArray(ids) ? ids : []).forEach((id) => {
    const text = id == null ? '' : String(id);
    if (text && !next.includes(text)) next.push(text);
  });
  selectedBranches = next;
}

export function setSelectedBranch(franchiseId) {
  const text = franchiseId == null ? '' : String(franchiseId);
  selectedBranches = text ? [text] : [];
}

export function getSelectedBranch() {
  return selectedBranches.length === 1 ? selectedBranches[0] : '';
}

// The shell turns this on only for pages whose lists follow the branch bar. Other pages keep the
// remembered choice but do not send it.
let branchFilterActive = false;

export function setBranchFilterActive(active) {
  branchFilterActive = Boolean(active);
}

function pickerQuery() {
  if (!branchFilterActive) return {};
  if (selectedBranches.length === 1) return { franchise_id: selectedBranches[0] };
  if (selectedBranches.length > 1) return { franchise_ids: selectedBranches.join(',') };
  return {};
}

/** Name franchise_id so this request uses that salon, or the organization, and not the shared bar. */
function namedBranch(franchiseId) {
  return { franchise_id: franchiseId ? String(franchiseId) : undefined };
}

function query(params = {}) {
  // A caller that names franchise_id or franchise_ids (even as undefined) replaces the picker.
  const explicitBranch = Object.prototype.hasOwnProperty.call(params, 'franchise_id')
    || Object.prototype.hasOwnProperty.call(params, 'franchise_ids');
  const merged = { ...(explicitBranch ? {} : pickerQuery()), ...params };
  const clean = Object.entries(merged).filter(([, v]) => v !== undefined && v !== null && v !== '');
  return clean.length ? `?${new URLSearchParams(clean)}` : '';
}

function portalHeader() {
  const portal = getEngagementPortal();
  return portal === 'partner' || portal === 'workspace' ? { [PORTAL_HEADER]: portal } : {};
}

async function request(path, { method = 'GET', body, form } = {}) {
  const init = { method, headers: { ...portalHeader() }, credentials: 'include' };
  if (form) {
    init.body = form;
  } else if (body !== undefined) {
    init.headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  const res = await fetch(`${getEngagementApiBase()}${path}`, init);
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    let detail = data && typeof data === 'object' ? data.detail : data;
    const extra = detail && typeof detail === 'object' && !Array.isArray(detail) ? detail : null;
    if (extra) detail = extra.message || JSON.stringify(extra);
    if (Array.isArray(detail)) detail = detail.map((d) => d.msg || JSON.stringify(d)).join(', ');
    const err = new Error(typeof detail === 'string' && detail ? detail : `Request failed (${res.status})`);
    err.status = res.status;
    err.code = extra?.code || '';
    err.data = extra;
    throw err;
  }
  return data;
}

/** Coerce list endpoints to arrays (handles null, or `{ staff: [...] }` wrappers from proxies). */
export function ensureArray(data, extraKeys = []) {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const keys = [...extraKeys, 'items', 'data', 'results', 'rows', 'list'];
    for (const key of keys) {
      if (Array.isArray(data[key])) return data[key];
    }
  }
  return [];
}

// Backend API version this frontend needs (see API_VERSION in app/main.py).
export const REQUIRED_API_VERSION = 25;

const api = {
  health: () => request('/health'),
  summary: () => request(`/summary${query()}`),

  // Who is signed in, which branches they see, what they may change
  me: () => request('/me'),
  branches: () => request('/branches'),

  // Analysis plan WhatsApp: the plan's messages, the sending number and the reminder settings
  analysisWaMe: () => request('/analysis-wa/me'),
  saveAnalysisWaSettings: (settings) => request('/analysis-wa/settings', { method: 'PUT', body: settings }),

  // Staff and slots (Staff Availability)
  listStaff: () => request(`/staff${query()}`).then((data) => ensureArray(data, ['staff'])),
  addStaff: (staff) => request('/staff', { method: 'POST', body: staff }),
  updateStaff: (id, patch) => request(`/staff/${id}`, { method: 'PATCH', body: patch }),

  listSlots: (params = {}) => request(`/slots${query(params)}`),
  slotsSummary: () => request(`/slots/summary${query()}`),
  addSlot: (slot) => request('/slots', { method: 'POST', body: slot }),
  generateSlots: (payload) => request('/slots/bulk', { method: 'POST', body: payload }),
  clearWeekdays: (staffId, weekdays) =>
    request(`/slots/bulk-clear${query({ staff_id: staffId, weekdays: weekdays.join(',') })}`, { method: 'DELETE' }),
  deleteSlot: (id) => request(`/slots/${id}`, { method: 'DELETE' }),
  blockSlot: (id) => request(`/slots/${id}/block`, { method: 'POST' }),
  unblockSlot: (id) => request(`/slots/${id}/unblock`, { method: 'POST' }),
  blockRange: (payload) => request('/slots/block-range', { method: 'POST', body: payload }),

  // Appointments
  listAppointments: () => request(`/appointments${query()}`).then((data) => ensureArray(data, ['appointments'])),
  markServiceTaken: (id) => request(`/appointments/${id}/service-taken`, { method: 'POST' }),
  markNoShow: (id) => request(`/appointments/${id}/no-show`, { method: 'POST' }),
  setAppointmentStatus: (id, status, reason = '') => request(`/appointments/${id}/status`, { method: 'POST', body: { status, reason } }),
  createAppointment: (payload) => request('/appointments', { method: 'POST', body: payload }),
  assignOptions: (id) => request(`/appointments/${id}/assign-options`),
  assignStylist: (id, slotId, notify = true) => request(`/appointments/${id}/assign`, { method: 'POST', body: { slot_id: slotId, notify } }),

  // Schedule (Staff Availability → Day schedule, walk-ins)
  scheduleDay: (day, franchiseId) => request(`/schedule/day${query({ day, ...(franchiseId ? { franchise_id: franchiseId } : {}) })}`),
  scheduleNow: (franchiseId) => request(`/schedule/now${query(franchiseId ? { franchise_id: franchiseId } : {})}`),
  lookupCustomer: (phone) => request(`/customers/lookup${query({ phone })}`),
  knownServices: () => request(`/services/known${query()}`),

  // The branch's service list (read-only, from the Naturals service list)
  listServices: (franchiseId) => request(`/services${query(franchiseId ? { franchise_id: franchiseId } : {})}`),
  searchServices: (q, franchiseId, gender) =>
    request(`/services/search${query({ q, gender, ...(franchiseId ? { franchise_id: franchiseId } : {}) })}`),

  // Customer profile (shared details, branches, preferences)
  customerProfile: (customerId) => request(`/customers/${customerId}/profile${query()}`),
  updateCustomer: (customerId, patch) => request(`/customers/${customerId}`, { method: 'PATCH', body: patch }),
  setCustomerPreferences: (customerId, prefs) => request(`/customers/${customerId}/preferences`, { method: 'PUT', body: prefs }),

  // Conversations and human intervention
  listConversations: (channel = 'whatsapp') =>
    request(`/conversations${query({ channel })}`).then((data) => ensureArray(data, ['conversations', 'threads'])),
  listMessages: (customerId, channel = 'whatsapp', conversationId = null) =>
    request(`/conversations/${customerId}/messages${query({ channel, conversation_id: conversationId })}`),
  sendReply: (customerId, body, channel = 'whatsapp', conversationId = null) =>
    request(`/conversations/${customerId}/reply${query()}`, { method: 'POST', body: { body, channel, conversation_id: conversationId } }),
  pauseAgent: (customerId, conversationId = null, channel = 'whatsapp') =>
    request(`/conversations/${customerId}/pause-agent${query({ conversation_id: conversationId, channel })}`, { method: 'POST' }),
  resumeAgent: (customerId, conversationId = null, channel = 'whatsapp') =>
    request(`/conversations/${customerId}/resume-agent${query({ conversation_id: conversationId, channel })}`, { method: 'POST' }),
  resetMemory: (customerId, conversationId = null, channel = 'whatsapp') =>
    request(`/conversations/${customerId}/reset-memory${query({ conversation_id: conversationId, channel })}`, { method: 'POST' }),

  listHandoffs: (status = 'open') => request(`/handoffs${query({ status })}`),
  resolveHandoff: (id) => request(`/handoffs/${id}/resolve`, { method: 'POST' }),
  getAlertNumbers: (franchiseId) => request(`/handoffs/alert-numbers${query(namedBranch(franchiseId))}`),
  saveAlertNumbers: (numbers, franchiseId) => request(`/handoffs/alert-numbers${query(namedBranch(franchiseId))}`, { method: 'PUT', body: { numbers } }),
  testAlert: (franchiseId) => request(`/handoffs/alert-numbers/test${query(namedBranch(franchiseId))}`, { method: 'POST' }),

  // Leads and calls
  listLeads: (params = {}) => request(`/leads${query(params)}`).then((data) => ensureArray(data, ['leads'])),
  updateLead: (id, patch) => request(`/leads/${id}`, { method: 'PATCH', body: patch }),
  assignLeadBranch: (id, franchiseId) => request(`/leads/${id}/assign-branch`, { method: 'POST', body: { franchise_id: franchiseId } }),
  listCalls: () => request(`/calls${query()}`).then((data) => ensureArray(data, ['calls'])),

  // AI Voice Call screen: the Sarvam agent rings a number
  outboundCallConfig: () => request('/outbound-calls/config'),
  startOutboundCall: (phone) =>
    request('/outbound-calls', { method: 'POST', body: { phone, franchise_id: getSelectedBranch() ? Number(getSelectedBranch()) : null } }),
  outboundCallStatus: (attemptId) => request(`/outbound-calls/status${query({ attempt_id: attemptId })}`),

  // Sent and waiting: a page of messages {items, total, offset, limit}; listJobs = just the items.
  listJobsPage: (params = {}) => request(`/jobs${query(params)}`),
  listJobs: (params = {}) => request(`/jobs${query(params)}`).then((data) => ensureArray(data, ['items'])),
  getInstagramConfig: () => request('/instagram/config'),
  saveInstagramConfig: (body) => request('/instagram/config', { method: 'PUT', body }),
  disconnectInstagram: () => request('/instagram/config', { method: 'DELETE' }),
  listInstagramAccounts: () => request('/instagram/accounts'),
  addInstagramAccount: (body) => request('/instagram/accounts', { method: 'POST', body }),
  updateInstagramAccount: (id, body) => request(`/instagram/accounts/${id}`, { method: 'PATCH', body }),
  disableInstagramAccount: (id) => request(`/instagram/accounts/${id}/disable`, { method: 'POST' }),
  enableInstagramAccount: (id) => request(`/instagram/accounts/${id}/enable`, { method: 'POST' }),
  checkInstagramAccount: (id) => request(`/instagram/accounts/${id}/check`, { method: 'POST' }),
  cancelJob: (id) => request(`/jobs/${id}/cancel`, { method: 'POST' }),
  cancelJobs: (ids) => request('/jobs/cancel-bulk', { method: 'POST', body: { ids } }),

  // HQ: agent settings (organization default and per-branch overrides) and cross-branch rules
  getAgentProfile: (channel = 'whatsapp', franchiseId) => request(`/hq/agent-profile${query({ channel, ...namedBranch(franchiseId) })}`),
  saveAgentProfile: (channel, changes, franchiseId) => request(`/hq/agent-profile${query({ channel, ...namedBranch(franchiseId) })}`, { method: 'PUT', body: changes }),
  getBranchRules: () => request('/hq/branch-rules'),
  saveBranchRules: (changes) => request('/hq/branch-rules', { method: 'PUT', body: changes }),

  // HQ: numbers (main number and branch WhatsApp numbers)
  listChannels: () => request('/channels'),
  addChannel: (payload) => request('/channels', { method: 'POST', body: payload }),
  updateChannel: (id, patch) => request(`/channels/${id}`, { method: 'PATCH', body: patch }),
  checkChannel: (id) => request(`/channels/${id}/check`, { method: 'POST' }),
  testChannel: (id, to) => request(`/channels/${id}/test`, { method: 'POST', body: { to } }),
  setupChannelTemplates: (id) => request(`/channels/${id}/templates/setup`, { method: 'POST' }),

  // General knowledge files for the WhatsApp agent (policies, offers, FAQs), kept in the OpenAI vector store
  listRag: () => request('/rag'),
  uploadRag: (file) => {
    const form = new FormData();
    form.append('file', file);
    return request('/rag', { method: 'POST', form });
  },
  deleteRag: (fileId) => request(`/rag/${encodeURIComponent(fileId)}`, { method: 'DELETE' }),
  createVectorStore: () => request('/rag/vector-store', { method: 'POST' }),

  // HQ: WhatsApp Business Accounts (tokens are never returned)
  listWaAccounts: () => request('/wa-accounts'),
  connectWaAccount: (payload) => request('/wa-accounts', { method: 'POST', body: payload }),
  embeddedSignup: (payload) => request('/wa-accounts/embedded-signup', { method: 'POST', body: payload }),
  updateWaAccount: (id, patch) => request(`/wa-accounts/${id}`, { method: 'PATCH', body: patch }),
  checkWaAccount: (id) => request(`/wa-accounts/${id}/check`, { method: 'POST' }),
  attachWaNumber: (id, payload) => request(`/wa-accounts/${id}/numbers`, { method: 'POST', body: payload }),
  removeWaAccount: (id) => request(`/wa-accounts/${id}`, { method: 'DELETE' }),

  // HQ: message templates (the registry of every WhatsApp account's templates)
  templateFields: () => request('/wa-templates/fields'),
  templateAccounts: () => request('/wa-templates/accounts'),
  listWaTemplates: (params = {}) => request(`/wa-templates${query({ ...params, franchise_id: undefined })}`),
  getWaTemplate: (id) => request(`/wa-templates/${id}`),
  createWaTemplate: (payload) => request('/wa-templates', { method: 'POST', body: payload }),
  updateWaTemplate: (id, patch) => request(`/wa-templates/${id}`, { method: 'PUT', body: patch }),
  submitWaTemplate: (id) => request(`/wa-templates/${id}/submit`, { method: 'POST' }),
  duplicateWaTemplate: (id, payload) => request(`/wa-templates/${id}/duplicate`, { method: 'POST', body: payload }),
  deleteWaTemplate: (id) => request(`/wa-templates/${id}`, { method: 'DELETE' }),
  syncWaTemplates: () => request('/wa-templates/sync', { method: 'POST' }),
  seedWaTemplates: (payload) => request('/wa-templates/seed', { method: 'POST', body: payload }),
  testWaTemplate: (id, phone) => request(`/wa-templates/${id}/test`, { method: 'POST', body: { phone } }),

  // HQ: automation builder
  automationCatalog: () => request('/automation-engine/catalog'),
  // Automations → Safety: the organization's sending rules (marketing pause and limit, test numbers, quiet hours,
  // "Did they come?", the visit report). Organization-wide: no branch.
  automationEngineSettings: () => request(`/automation-engine/settings${query(namedBranch(''))}`),
  saveAutomationEngineSettings: (changes) => request(`/automation-engine/settings${query(namedBranch(''))}`, { method: 'PUT', body: changes }),
  // The organization's automations; their 30-day numbers count the branches picked.
  listAutomations: (status) => request(`/automations${query({ status })}`),
  getAutomation: (id) => request(`/automations/${id}`),
  createAutomation: (payload) => request('/automations', { method: 'POST', body: payload }),
  saveAutomation: (id, patch) => request(`/automations/${id}`, { method: 'PUT', body: patch }),
  publishAutomation: (id, activate = false) => request(`/automations/${id}/publish?activate=${activate ? 'true' : 'false'}`, { method: 'POST' }),
  activateAutomation: (id) => request(`/automations/${id}/activate`, { method: 'POST' }),
  pauseAutomation: (id, stopRuns = false) => request(`/automations/${id}/pause?stop_runs=${stopRuns ? 'true' : 'false'}`, { method: 'POST' }),
  archiveAutomation: (id) => request(`/automations/${id}/archive`, { method: 'POST' }),
  duplicateAutomation: (id) => request(`/automations/${id}/duplicate`, { method: 'POST' }),
  deleteAutomation: (id) => request(`/automations/${id}`, { method: 'DELETE' }),
  resetAutomation: (id) => request(`/automations/${id}/reset`, { method: 'POST' }),
  listRecipes: () => request('/automation-recipes'),
  createFromRecipe: (key) => request('/automations/from-recipe', { method: 'POST', body: { key } }),
  testAutomation: (id, payload) => request(`/automations/${id}/test`, { method: 'POST', body: payload }),
  // People in a flow: a page of runs {items, total, offset, limit}.
  listRuns: (params = {}) => request(`/automation-runs${query(params)}`),
  getRun: (id) => request(`/automation-runs/${id}`),
  stopRun: (id) => request(`/automation-runs/${id}/stop`, { method: 'POST' }),
  pauseRun: (id) => request(`/automation-runs/${id}/pause`, { method: 'POST' }),
  resumeRun: (id) => request(`/automation-runs/${id}/resume`, { method: 'POST' }),
  retryJob: (id) => request(`/jobs/${id}/retry`, { method: 'POST' }),
  pauseCustomerAutomations: (customerId, hours) =>
    request(`/customers/${customerId}/automation-pause`, { method: 'POST', body: { hours: hours || null } }),
  resumeCustomerAutomations: (customerId) => request(`/customers/${customerId}/automation-resume`, { method: 'POST' }),
  listOptedOut: (q) => request(`/customers/opted-out${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  setOptOutByPhone: (phone, optedOut) => request('/customers/opt-out', { method: 'POST', body: { phone, opted_out: optedOut } }),

  // Bookings with several services (one line per service)
  bookingOptions: (params) => request(`/bookings/options${query(params)}`),
  createBooking: (payload) => request('/bookings', { method: 'POST', body: payload }),
  getBooking: (id) => request(`/bookings/${id}`),
  addBookingLine: (id, payload) => request(`/bookings/${id}/lines`, { method: 'POST', body: payload }),
  cancelBookingLine: (appointmentId, reason) => request(`/appointments/${appointmentId}/cancel`, { method: 'POST', body: { reason } }),
  cancelBooking: (id, reason) => request(`/bookings/${id}/cancel`, { method: 'POST', body: { reason } }),
  moveBooking: (id, slotIds, notify = true) => request(`/bookings/${id}/move`, { method: 'POST', body: { slot_ids: slotIds, notify } }),
  extendAppointment: (id) => request(`/appointments/${id}/extend`, { method: 'POST' }),
  shortenAppointment: (id) => request(`/appointments/${id}/shorten`, { method: 'POST' }),

  // Adding a service in the salon (clash options, running-late requests, upsell report)
  addServiceOptions: (bookingId, params = {}) =>
    request(`/bookings/${bookingId}/add-service/options${query({ ...params, franchise_id: undefined })}`),
  addService: (bookingId, payload) => request(`/bookings/${bookingId}/add-service`, { method: 'POST', body: payload }),
  listAddonRequests: (status = 'waiting') => request(`/addon-requests${query({ status })}`),
  cancelAddonRequest: (id) => request(`/addon-requests/${id}/cancel`, { method: 'POST' }),
  upsellReport: (params = {}) => request(`/reports/in-salon-upsells${query(params)}`),

  // Offer rules (upsell, downsell and in-salon add-on pairs) and the offer report
  listOfferRules: (params = {}) => request(`/offer-rules${query(params)}`),
  createOfferRule: (payload) => request('/offer-rules', { method: 'POST', body: payload }),
  updateOfferRule: (id, payload) => request(`/offer-rules/${id}`, { method: 'PUT', body: payload }),
  deleteOfferRule: (id) => request(`/offer-rules/${id}`, { method: 'DELETE' }),
  tryOffer: (params) => request(`/offer-rules/try${query(params)}`),
  offerReport: (params = {}) => request(`/reports/offers${query(params)}`),

  // HQ: campaigns, saved audiences, coupon codes and the ad registry. These do not send WhatsApp.
  listCampaigns: () => request('/campaigns').then((data) => ensureArray(data, ['campaigns'])),
  createCampaign: (payload) => request('/campaigns', { method: 'POST', body: payload }),
  getCampaign: (id) => request(`/campaigns/${id}`),
  prepareCampaign: (id) => request(`/campaigns/${id}/prepare`, { method: 'POST' }),
  pauseCampaign: (id) => request(`/campaigns/${id}/pause`, { method: 'POST' }),
  cancelCampaign: (id) => request(`/campaigns/${id}/cancel`, { method: 'POST' }),

  listSegments: () => request('/segments').then((data) => ensureArray(data, ['segments'])),
  createSegment: (payload) => request('/segments', { method: 'POST', body: payload }),
  previewSegment: (rules) => request('/segments/preview', { method: 'POST', body: { rules } }),

  listCouponOffers: () => request('/coupon-offers').then((data) => ensureArray(data, ['offers', 'coupon_offers'])),
  createCouponOffer: (payload) => request('/coupon-offers', { method: 'POST', body: payload }),
  deleteCouponOffer: (id) => request(`/coupon-offers/${id}`, { method: 'DELETE' }),

  listAdCampaigns: () => request('/ad-campaigns').then((data) => ensureArray(data, ['ad_campaigns', 'campaigns'])),
  createAdCampaign: (payload) => request('/ad-campaigns', { method: 'POST', body: payload }),
  getAdCampaign: (id) => request(`/ad-campaigns/${id}`),
  adPrefill: (id) => request(`/ad-campaigns/${id}/prefill`),
  mockAdClick: (id, payload) => request(`/ad-campaigns/${id}/mock-click`, { method: 'POST', body: payload }),
  listUnlinkedAds: () => request('/ad-campaigns/unlinked').then((data) => ensureArray(data, ['ads', 'unlinked'])),
  linkUnlinkedAd: (adRowId, adCampaignId) =>
    request(`/ad-campaigns/unlinked/${adRowId}/link`, { method: 'POST', body: { ad_campaign_id: adCampaignId } }),
};

export default api;

// ── formatting ───────────────────────────────────────────────────────────────

function toDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDateTime(value) {
  const d = toDate(value);
  if (!d) return value ? String(value) : '—';
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function formatDate(value) {
  const d = toDate(value);
  if (!d) return value ? String(value) : '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function formatTime(value) {
  const d = toDate(value);
  if (!d) return '—';
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

export function formatPhone(value) {
  if (!value) return '—';
  const raw = String(value).trim();
  if (raw.startsWith('+')) return raw;
  if (/^\d+$/.test(raw)) return raw.length === 10 ? `+91${raw}` : `+${raw}`;
  return raw;
}

/** "15:30" → "3:30 PM". */
export function clockLabel(hhmm) {
  const [h, m] = String(hhmm || '').split(':').map(Number);
  if (Number.isNaN(h)) return hhmm || '';
  return `${((h + 11) % 12) + 1}:${String(m || 0).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

/** Local calendar date as YYYY-MM-DD (not UTC). */
export function isoDate(d = new Date()) {
  const local = new Date(d);
  local.setMinutes(local.getMinutes() - local.getTimezoneOffset());
  return local.toISOString().slice(0, 10);
}

export function addDays(iso, days) {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  return isoDate(d);
}
